<template>
  <spinner v-if="loading" />
  <div v-else>
    <div v-if="!openCheckout" class="store-wrapper">
      <div class="store-container">
        <h1 id="title">ZKM webshop</h1>
        <div class="store-items">
          <div class="left-container">
            <user-container :user="user" @refetchUser="getUser" />
            <store-item-container
              :enable-store="!!user"
              :organization-slug="organizationSlug"
              :initial-values="order"
              @updateOrder="updateOrder"
            />
          </div>
          <shopping-cart
            class="shopping-cart"
            :order="order"
            :show-next="!!user"
            :show-edit="false"
            @next="handleNextAction"
          />
        </div>
      </div>
      <simple-snackbar v-bind.sync="snackbar" />
    </div>
    <checkout-container
      v-else
      ref="checkoutContainer"
      :organization="organization"
      :user="user"
      :buyer="buyer"
      :started-at="startedAt"
      :order="order"
      @refetchUser="getUser"
      @edit="handleOpenCheckout"
      @submit="handleSubmit"
    />
  </div>
</template>

<script>
import UserContainer from './user/UserContainer.vue'
import StoreItemContainer from './store-items/StoreItemContainer.vue'
import CheckoutContainer from './store-checkout/CheckoutContainer.vue'
import ShoppingCart from './shopping-cart/ShoppingCart.vue'
import { CREATE_ORGANIZATION_STORE_ORDER } from '@/graphql/order.graphql'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import { GET_LOGGED_USER } from '@/graphql/user.graphql'
import { GET_ORGANIZATION_BY_SLUG } from '@/graphql/organization.graphql'
import { PaymentMethod } from '@/types'
import moment from 'moment'
import { getOrganizationStoreOrderInfoByUUID, populateOrganizationStoreOrder } from '@/http'
import { createPurchaseSubmitFormWSPay } from '@/utils'

export default {
  name: 'register',
  components: {
    SimpleSnackbar,
    CheckoutContainer,
    UserContainer,
    StoreItemContainer,
    ShoppingCart,
  },
  props: {
    organizationSlug: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      openCheckout: false,
      order: [],
      orderUUID: null,
      snackbar: {},
      user: null,
      organization: null,
      buyer: {},
      startedAt: null,
      loading: true,
    }
  },
  mounted() {
    this.getUser()
    this.getOrganization()
  },
  methods: {
    updateOrder(items) {
      return (this.order = items)
    },
    handleOpenCheckout() {
      this.openCheckout = !this.openCheckout
    },
    async handleNextAction(price) {
      const orderItemIds = this.order.map(oi => oi.id)

      const { errors, data } = await this.$apollo.mutate({
        mutation: CREATE_ORGANIZATION_STORE_ORDER,
        variables: { order: { orderItemIds, price } },
      })
      if (errors) {
        return this.handleOpenSnackbar(errors[0].message, 'error')
      }
      const { createOrganizationStoreOrder } = data
      localStorage.setItem(
        'organizationStoreOrder',
        JSON.stringify({
          UUID: createOrganizationStoreOrder.uuid,
          startedAt: new Date(),
        }),
      )
      this.startedAt = new Date().toISOString()
      this.orderUUID = createOrganizationStoreOrder.uuid
      this.handleOpenCheckout()
    },
    async getUser() {
      const { data } = await this.$apollo.query({
        query: GET_LOGGED_USER,
      })
      if (data.me) {
        this.user = data.me
        this.buyer = {
          paymentMethod: PaymentMethod.CreditCard,
          firstName: this.user.firstName,
          lastName: this.user.lastName,
          email: this.user.email,
        }
      }
    },
    async parseSavedOrder(organizationId) {
      const savedOrder = localStorage.getItem('organizationStoreOrder')
      if (!savedOrder) {
        return null
      }
      const order = JSON.parse(savedOrder)
      const { UUID, startedAt } = order
      const differenceInMinutes = moment(new Date()).diff(moment(startedAt), 'minutes')
      if (differenceInMinutes >= 15) {
        this.handleClearLocalStorage()

        return null
      }
      this.startedAt = startedAt
      this.orderUUID = UUID
      const found = await getOrganizationStoreOrderInfoByUUID(UUID)
      if (found.purchases[0].orderItem.organizationId !== organizationId.toString()) {
        return null
      }
      this.updateOrder(found.purchases.map(p => p.orderItem))

      return this.handleOpenCheckout()
    },
    handleOpenSnackbar(msg, color) {
      return (this.snackbar = {
        open: true,
        messageProp: msg,
        color,
      })
    },
    async getOrganization() {
      const { data } = await this.$apollo.query({
        query: GET_ORGANIZATION_BY_SLUG,
        variables: { organizationSlug: this.organizationSlug },
      })
      if (data) {
        this.organization = data.getOrganizationBySlug
        await this.parseSavedOrder(this.organization.id)
        this.loading = false
      }
    },
    handleClearLocalStorage() {
      localStorage.removeItem('organizationStoreOrder')
    },
    async handleSubmit() {
      const { acceptTermsAndConditions, ...rest } = this.buyer
      const { data, errors } = await populateOrganizationStoreOrder({
        orderUUID: this.orderUUID,
        buyer: { ...rest },
      })
      if (errors) {
        return this.handleOpenSnackbar(errors[0].message, 'error')
      }
      this.handleClearLocalStorage()
      const price = this.$refs.checkoutContainer.$refs.shoppingCart.getTotalPrice()
      if (!price) {
        return this.$router.push({
          name: 'successful-payment',
          params: { orderUUID: this.orderUUID },
        })
      }
      const { populateOrganizationStoreOrder: orderInfo } = data
      const tickets = this.order
      const buyerInfo = {
        attendee: { ...rest, street: rest.address },
        paymentMethod: this.buyer.paymentMethod,
      }
      const form = await createPurchaseSubmitFormWSPay(
        buyerInfo,
        `EUR ${price}`,
        { id: orderInfo.id },
        'HR',
        tickets,
      )
      document.body.appendChild(form)
      form.submit()
    },
  },
}
</script>

<style scoped>
.store-wrapper {
  display: flex;
  justify-content: center;
  padding: 2rem 7rem;
}
.store-items {
  display: flex;
  gap: 1.5rem;
}
.store-container {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}
#title {
  color: #333;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Rubik;
  font-size: 36px;
  font-style: normal;
  font-weight: 550;
  line-height: 120%; /* 43.2px */
  letter-spacing: -1.08px;
}
.left-container {
  flex: 2;
  width: 800px;
}
.shopping-cart {
  flex: 1;
  width: 400px;
}
@media screen and (max-width: 1260px) {
  .store-items {
    flex-direction: column;
  }
  .left-container {
    width: 100%;
  }
  .shopping-cart {
    width: 100%;
  }
  .store-wrapper {
    padding: 0 2rem;
  }
}
@media screen and (max-width: 800px) {
  .store-wrapper {
    padding: 0;
  }
}
</style>
